import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { twMerge } from 'tailwind-merge';
import { ConversationMessageKey } from 'flow/types/TicketEventData';
import { TicketKey } from 'flow/types/EntityKeys';
import {
    selectEventDirection,
    selectEventDTOSubtype,
    selectEventTimestamp,
    selectMessageDeliveryStatusIsSending,
} from 'flow/selectors/TicketEventSelectors';
import useAppContext from 'flow/hooks/useAppContext';
import MessageItemSent from 'flow/components/message/MessageItemSent';
import MessageSender from 'flow/components/tickets/MessageSender';
import SenderAvatarExternalService from 'flow/components/tickets/event-item/Messages/SenderAvatarExternalService';
import AttachmentsContainer from 'flow/components/tickets/event-item/Messages/AttachmentsContainer';
import DeliveryStatus from 'flow/components/tickets/event-item/Messages/DeliveryStatus';
import MessageStyled from 'flow/components/tickets/event-item/Messages/MessageStyled';
import Tooltip from 'flow/components/ui-components/Tooltip';
import { eventIsSupportedForDisplay } from 'flow/utils/ticket-utils';

interface Props {
    ticketKey: TicketKey;
    messageKey: ConversationMessageKey;
    onNewAttachmentsFetched?: () => void;
}

const MessageExternalServiceV2 = ({
    ticketKey,
    messageKey,
    onNewAttachmentsFetched,
}: Props) => {
    const { i18next } = useAppContext();

    const dtoSubtype = useAppSelector((state) =>
        selectEventDTOSubtype(state, ticketKey, messageKey),
    );
    const direction = useAppSelector((state) =>
        selectEventDirection(state, ticketKey, messageKey),
    );
    const timestamp = useAppSelector((state) =>
        selectEventTimestamp(state, ticketKey, messageKey),
    );
    const deliveryStatusIsSending = useAppSelector((state) =>
        selectMessageDeliveryStatusIsSending(state, ticketKey, messageKey),
    );

    const channelLabel = getChannelLabel(dtoSubtype);
    const unsupportedType = !eventIsSupportedForDisplay(dtoSubtype);

    return (
        <div
            className={twMerge(
                'bg-gray-150 flex flex-col gap-1 rounded-xl p-3',
                deliveryStatusIsSending && 'animate-fade opacity-50',
            )}
        >
            <div className="flex h-6 items-center gap-2 text-sm">
                <SenderAvatarExternalService
                    ticketKey={ticketKey}
                    messageKey={messageKey}
                    size="small"
                />
                <MessageSender ticketKey={ticketKey} messageKey={messageKey} />
                {channelLabel && (
                    <span className="text-sm text-gray-500">
                        {channelLabel}
                    </span>
                )}
                {unsupportedType ? <UnsupportedTypeLabel /> : null}
                <span className="ms-auto text-sm text-gray-500">
                    <MessageItemSent timeString={timestamp} />
                </span>
            </div>
            <div className="ps-8 wrap-break-word whitespace-pre-wrap">
                <div className="min-w-0 text-base wrap-break-word text-gray-800 no-underline">
                    {dtoSubtype === 'OmniWhatsAppUnsupportedTicketEvent' ? (
                        <div className="text-gray-400 italic">
                            {i18next.t('tickets.message.content_unsupported')}
                        </div>
                    ) : (
                        <MessageStyled
                            ticketKey={ticketKey}
                            messageKey={messageKey}
                        />
                    )}
                    <AttachmentsContainer
                        ticketKey={ticketKey}
                        eventKey={messageKey}
                        onNewAttachmentsFetched={onNewAttachmentsFetched}
                        isBeingSent={deliveryStatusIsSending}
                    />
                </div>
                {direction === 'OUTGOING' && (
                    <DeliveryStatus
                        messageKey={messageKey}
                        ticketKey={ticketKey}
                    />
                )}
            </div>
        </div>
    );
};

export default React.memo(MessageExternalServiceV2);

const UnsupportedTypeLabel = () => {
    const { i18next } = useAppContext();
    const tooltip = i18next.t('tickets.message.unsupported_type.tooltip');

    return (
        <Tooltip label={tooltip}>
            <button
                type="button"
                className="focus-ring cursor-default rounded-sm text-sm text-gray-500"
            >
                {i18next.t('tickets.message.unsupported_type')}
            </button>
        </Tooltip>
    );
};

const getChannelLabel = (dtoSubtype: string): string | undefined => {
    if (
        dtoSubtype === 'OmniWhatsAppMessageTicketEvent' ||
        dtoSubtype === 'OmniWhatsAppUnsupportedTicketEvent'
    )
        return 'WhatsApp';
    return undefined;
};
